import { deleteFridge, deleteSensor, useFridges, useSensors } from '@gozoki/api'
import { Button, Paper, Tab, Tabs, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { PlusSquare } from 'lucide-react'
import { useMemo, useState } from 'react'
import z from 'zod/v3'
import { ConfirmButton } from '../../../components/communication/ConfirmButton'
import Page from '../../../components/Page'
import FridgeGrid from '../../../components/routes/sensors/FridgeGrid'
import SensorChart from '../../../components/routes/sensors/SensorChart'
import SensorGrid from '../../../components/routes/sensors/SensorGrid'
import SensorStatusCards from '../../../components/routes/sensors/SensorStatusCards'
import { StoresPicker } from '../../../utils/dashboard/storesPicker'
import { useRights } from '../../../utils/hooks'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

const tabs = ['sensors', 'fridges'] as const

export const Route = createFileRoute('/_authenticated/sensors/')({
  component: SensorsPage,
  validateSearch: z.object({
    tab: z.enum(tabs).optional(),
  }),
})

function SensorsPage() {
  const navigate = Route.useNavigate()
  const { tab = 'sensors' } = Route.useSearch()

  const queryClient = useQueryClient()

  const [currentStore, setCurrentStore] = useState<number | null>(null)
  const [selectedSensorIds, setSelectedSensorIds] = useState<number[]>([])
  const [selectedFridgeIds, setSelectedFridgeIds] = useState<number[]>([])
  const [currentPageSensorIds, setCurrentPageSensorIds] = useState<number[]>([])

  const { data: sensors, isLoading } = useSensors(currentStore)
  const { data: fridges, isLoading: isLoadingFridges } = useFridges(currentStore)
  const isCpiGlobalOrSuperAdmin = useRights(['CPI_GLOBAL', 'SUPER_ADMIN'])

  // Get temperature thresholds (using default values for now)
  const temperatureThresholdMin = 0
  const temperatureThresholdMax = 6

  // Get sensor IDs from selected fridges
  const sensorIdsFromFridges = useMemo(() => {
    if (selectedFridgeIds.length === 0 || !fridges || !sensors) {
      return []
    }

    // Get fridge references from selected fridge IDs
    const selectedFridgeReferences = new Set(
      fridges
        .filter((fridge) => selectedFridgeIds.includes(fridge.id))
        .map((fridge) => fridge.reference),
    )

    // Get sensor IDs that belong to selected fridges
    return sensors
      .filter((sensor) => sensor.fridge && selectedFridgeReferences.has(sensor.fridge.reference))
      .map((sensor) => sensor.id)
  }, [selectedFridgeIds, fridges, sensors])

  // Combine selected sensor IDs with sensors from selected fridges
  const allSelectedSensorIds = useMemo(() => {
    const combined = [...selectedSensorIds, ...sensorIdsFromFridges]
    // Remove duplicates
    return [...new Set(combined)]
  }, [selectedSensorIds, sensorIdsFromFridges])

  const handleChange = (newValue: number) => {
    const newTab = tabs.at(newValue) ?? 'sensors'
    setSelectedSensorIds([])
    setSelectedFridgeIds([])
    navigate({ search: { tab: newTab } })
  }

  // Delete mutations
  const deleteSensorMut = useMutation({
    mutationFn: deleteSensor,
    onSuccess: () => {
      // Invalidate sensors queries to refetch the list
      queryClient.invalidateQueries({ queryKey: ['sensors'] })
      setSelectedSensorIds([])
    },
  })

  const deleteFridgeMut = useMutation({
    mutationFn: deleteFridge,
    onSuccess: () => {
      // Invalidate fridges query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['fridges'] })
      setSelectedFridgeIds([])
    },
  })

  return (
    <Page>
      <div style={{ display: 'flex', paddingBottom: 10, paddingLeft: 10 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Équipements de Réfrigération
        </Typography>
        {tab === 'sensors' && selectedSensorIds.length === 1 && (
          <>
            <ConfirmButton
              buttonText="Supprimer ce capteur"
              buttonProps={{
                disabled: !isCpiGlobalOrSuperAdmin || deleteSensorMut.isPending,
                sx: {
                  marginRight: '16px',
                  height: '42px',
                  ...ButtonOverrides.redOutlined,
                },
                variant: 'outlined',
              }}
              description="Etes-vous sûrs de vouloir supprimer ce capteur ? Cette action est irréversible et supprimera toutes les données associées à ce capteur."
              onConfirm={() => deleteSensorMut.mutateAsync(selectedSensorIds[0])}
              title="Supprimer ce capteur ?"
              confirmButton={
                deleteSensorMut.isPending
                  ? {
                      loading: true,
                      text: 'Suppression en cours     ',
                    }
                  : { text: 'Supprimer' }
              }
            />
            <Button
              variant="contained"
              sx={{ marginRight: '16px' }}
              disabled={!isCpiGlobalOrSuperAdmin}
              onClick={() =>
                navigate({
                  search: { sensorId: selectedSensorIds[0] },
                  to: '/sensors/new',
                })
              }
            >
              Modifier ce capteur
            </Button>
          </>
        )}
        {tab === 'fridges' && selectedFridgeIds.length === 1 && (
          <>
            <ConfirmButton
              buttonText="Supprimer ce réfrigérateur"
              buttonProps={{
                disabled: !isCpiGlobalOrSuperAdmin || deleteFridgeMut.isPending,
                sx: {
                  marginRight: '16px',
                  height: '42px',
                  ...ButtonOverrides.redOutlined,
                },
                variant: 'outlined',
              }}
              description="Etes-vous sûrs de vouloir supprimer ce réfrigérateur ? Cette action est irréversible et supprimera toutes les données associées à ce réfrigérateur."
              onConfirm={() => deleteFridgeMut.mutateAsync(selectedFridgeIds[0])}
              title="Supprimer ce réfrigérateur ?"
              confirmButton={
                deleteFridgeMut.isPending
                  ? {
                      loading: true,
                      text: 'Suppression en cours     ',
                    }
                  : { text: 'Supprimer' }
              }
            />
            <Button
              variant="contained"
              sx={{ marginRight: '16px' }}
              disabled={!isCpiGlobalOrSuperAdmin}
              onClick={() =>
                navigate({
                  search: { fridgeId: selectedFridgeIds[0] },
                  to: '/sensors/fridge/new',
                })
              }
            >
              Modifier ce réfrigérateur
            </Button>
          </>
        )}
        {tab === 'sensors' && (
          <Button
            variant="contained"
            sx={{ marginRight: '16px' }}
            disabled={!isCpiGlobalOrSuperAdmin}
            onClick={() => navigate({ to: '/sensors/new' })}
          >
            <PlusSquare />
            Ajouter un capteur
          </Button>
        )}
        {tab === 'fridges' && (
          <Button
            variant="contained"
            sx={{ marginRight: '16px' }}
            disabled={!isCpiGlobalOrSuperAdmin}
            onClick={() => navigate({ to: '/sensors/fridge/new' })}
          >
            <PlusSquare />
            Ajouter un réfrigérateur
          </Button>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <StoresPicker
          setUniqueStore={setCurrentStore}
          allowAllStores
          onlyBoutiques
          defaultStore={currentStore !== null ? currentStore : undefined}
        />
      </div>
      <SensorStatusCards sensors={sensors} />
      <Tabs
        value={tabs.indexOf(tab)}
        onChange={(_, newValue) => handleChange(newValue)}
        sx={{ marginBottom: 1 }}
      >
        <Tab label="Capteurs" />
        <Tab label="Réfrigérateurs" />
      </Tabs>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        {tab === 'sensors' && (
          <SensorGrid
            data={sensors}
            loading={isLoading && !sensors}
            onSelectionChange={setSelectedSensorIds}
            onPageChange={setCurrentPageSensorIds}
          />
        )}
        {tab === 'fridges' && (
          <FridgeGrid
            data={fridges}
            loading={isLoadingFridges && !fridges}
            onSelectionChange={setSelectedFridgeIds}
          />
        )}
      </Paper>
      <SensorChart
        selectedSensorIds={allSelectedSensorIds}
        isFridgeSelected={selectedFridgeIds.length > 0}
        storeId={currentStore}
        temperatureThresholdMin={temperatureThresholdMin}
        temperatureThresholdMax={temperatureThresholdMax}
        currentPageSensorIds={currentPageSensorIds}
      />
      <div style={{ margin: 10 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {tab === 'sensors'
            ? 'Sélectionnez des capteurs dans le tableau pour afficher leur historique'
            : "Sélectionnez des réfrigérateurs dans le tableau pour afficher l'historique de leurs capteurs"}
        </Typography>
      </div>
    </Page>
  )
}
